import { useEffect, useLayoutEffect, useState } from 'react';

// Which element scroll restoration should save/restore. `null` means the
// window (the default for normal document-scroll pages, including the mobile
// board). A page that scrolls an INNER container instead — e.g. the desktop
// board's `FeedScroll` div, which lives in a fixed 100dvh shell so the window
// never scrolls — registers that element here so useScrollRestoration tracks
// it instead of the window.
//
// The store is parked on `window`, NOT a module-level `let`. useScrollRestoration
// lives in the eagerly-loaded main bundle while the registering page
// (BoardLayout) is a lazy-loaded chunk; a module-level singleton is not
// guaranteed to be the SAME instance across that chunk boundary (webpack can
// end up with two copies), which would silently break the hand-off — the
// registrar writes one copy, the reader reads the other. A single object on
// `window` is shared no matter how the bundle is split. (This was the bug:
// desktop registration looked wired but never reached the manager.)

interface ContainerStore {
  el: HTMLElement | null;
  subscribers: Set<() => void>;
}

function store(): ContainerStore {
  const w = window as unknown as { __theaScrollContainerStore?: ContainerStore };
  if (!w.__theaScrollContainerStore) {
    w.__theaScrollContainerStore = { el: null, subscribers: new Set() };
  }
  return w.__theaScrollContainerStore;
}

function setActiveScrollContainer(el: HTMLElement | null): void {
  const s = store();
  if (s.el === el) return;
  s.el = el;
  s.subscribers.forEach((notify) => notify());
}

/**
 * Register `el` as the active scroll container for as long as the calling
 * component is mounted; clears back to the window (null) on unmount. Pass the
 * element (not a ref) so registration tracks the element's actual lifecycle —
 * callers typically hold it in state via a callback ref.
 *
 * useLayoutEffect, not useEffect (THE-1140 QA follow-up on #868): on a route
 * change that unmounts this registrant (the desktop board's FeedScroll) in
 * the SAME commit that mounts a page using useScrollRestoration (People), a
 * passive effect's cleanup runs AFTER that commit's layout effects — so
 * useScrollRestoration's own (layout-effect) restore would still read the
 * about-to-be-detached board container for one extra commit, apply the
 * restore to it (a no-op — nothing visible is listening anymore), and only
 * pick up the real target (the window) on the FOLLOWING commit. That gap is
 * exactly the one extra frame QA's CDP screencast caught on desktop Back: a
 * real paint at scroll-top 0, then the correct offset a few ms later. A
 * layout effect's cleanup runs in the same pre-paint phase as the new
 * route's layout effects, closing the gap.
 */
export function useRegisterScrollContainer(el: HTMLElement | null): void {
  useLayoutEffect(() => {
    setActiveScrollContainer(el);
    return () => {
      // Only relinquish if we're still the active one — guards against a
      // newer registrant being clobbered by an older one's late cleanup.
      if (store().el === el) setActiveScrollContainer(null);
    };
  }, [el]);
}

/**
 * A synchronous, un-subscribed read of the active container — the live
 * value, not a React-state snapshot. useScrollRestoration's restore effect
 * reads this (THE-1140 QA follow-up on #868) instead of trusting its
 * `container` prop/dep on the commit where the old registrant unmounts:
 * that prop is whatever `useActiveScrollContainer`'s state was AT RENDER
 * TIME, which is captured before this same commit's unregister cleanup
 * (now a layout effect, see above) has run — structurally always one step
 * behind on that specific commit, no matter which effect type unregisters.
 * Reading the store directly, inside the layout effect's own callback
 * (which runs after deletions in the same commit have already cleaned up),
 * sees the correct value immediately — no cascading extra commit needed.
 */
export function getActiveScrollContainer(): HTMLElement | null {
  return store().el;
}

/**
 * The active scroll container (or null for the window), re-rendering the
 * caller when it changes. Read by useScrollRestoration so a container that
 * registers AFTER the route renders (the board mounts, then its feed registers
 * in an effect) still gets its scroll restored.
 */
export function useActiveScrollContainer(): HTMLElement | null {
  const [el, setEl] = useState<HTMLElement | null>(() => store().el);
  useEffect(() => {
    const s = store();
    const notify = () => setEl(s.el);
    s.subscribers.add(notify);
    notify(); // sync up in case it changed between render and subscribe
    return () => {
      s.subscribers.delete(notify);
    };
  }, []);
  return el;
}
